export type WrenchRoute = {
  path: string;
  title: string;
  description: string;
  /** Free-text keywords that should match in the palette. */
  keywords: string;
  category: "tool" | "page";
  group: "creator" | "support" | "account" | "info";
};

/**
 * Searchable index of every route Wrench should be able to navigate to. Keep
 * descriptions short — they're rendered under the title in the palette.
 */
export const WRENCH_ROUTES: WrenchRoute[] = [
  {
    path: "/hub",
    title: "Studio Hub",
    description: "Categorized launcher for every Workshop tool.",
    keywords: "hub home dashboard launcher start studio",
    category: "page",
    group: "creator",
  },
  // Creator tools
  {
    path: "/paintbooth-studio",
    title: "Paint Booth Studio",
    description: "Photoshop-style editor for liveries and textures.",
    keywords: "studio livery texture editor canvas paint psd ytd photoshop layers",
    category: "tool",
    group: "creator",
  },
  {
    path: "/paintbooth",
    title: "Paint Booth",
    description: "Live preview of liveries on 3D vehicles.",
    keywords: "preview vehicle livery skin paint",
    category: "tool",
    group: "creator",
  },
  {
    path: "/livery-magic",
    title: "Livery Magic",
    description: "AI-assisted livery generator.",
    keywords: "ai livery generate paint vehicle",
    category: "tool",
    group: "creator",
  },
  {
    path: "/model-forge",
    title: "Model Forge",
    description: "Generate 3D props and models.",
    keywords: "props 3d model generate forge tripo",
    category: "tool",
    group: "creator",
  },
  {
    path: "/code-smith",
    title: "Code Smith",
    description: "Build and edit Lua / JS scripts for your server.",
    keywords: "scripts lua javascript fivem code",
    category: "tool",
    group: "creator",
  },
  {
    path: "/code-visualizer",
    title: "Code Visualizer",
    description: "Map FiveM scripts into readable command, event, export, and function blocks.",
    keywords: "scripts lua javascript fivem code visualizer blocks events commands exports functions map",
    category: "tool",
    group: "creator",
  },
  {
    path: "/script-docs",
    title: "Script Docs",
    description: "Generate README-ready docs from FiveM resources.",
    keywords: "scripts lua documentation readme resource events exports commands",
    category: "tool",
    group: "creator",
  },
  {
    path: "/easy-meta",
    title: "Easy Meta",
    description: "Generate vehicles.meta, carcols.meta and friends.",
    keywords: "meta xml vehicle carcols handling",
    category: "tool",
    group: "creator",
  },
  {
    path: "/blinklab",
    title: "Blink Lab",
    description: "Edit ELS / Sirenmaster light patterns.",
    keywords: "lights siren els carcols pattern",
    category: "tool",
    group: "creator",
  },
  {
    path: "/blinklab/carcols",
    title: "Blink Lab — Carcols",
    description: "Carcols-specific light pattern workflow.",
    keywords: "carcols sirenmaster els lights",
    category: "tool",
    group: "creator",
  },
  {
    path: "/chevron-builder",
    title: "Chevron Builder",
    description: "Generate rear warning chevron patterns for emergency vehicles.",
    keywords: "chevron rear warning stripes emergency fire ems dot tow livery pattern",
    category: "tool",
    group: "creator",
  },
  {
    path: "/uv-studio",
    title: "UV Studio",
    description: "Inspect and tweak UV maps.",
    keywords: "uv unwrap map texture",
    category: "tool",
    group: "creator",
  },
  {
    path: "/pack-bench",
    title: "Pack Bench",
    description: "Optimize and package FiveM resources for shipping.",
    keywords: "pack optimize compress build resource",
    category: "tool",
    group: "creator",
  },
  {
    path: "/addon-eup-creator",
    title: "Add-on EUP Creator",
    description: "Build an EUP add-on package.",
    keywords: "eup ped clothing addon",
    category: "tool",
    group: "creator",
  },
  {
    path: "/eup-viewer",
    title: "EUP Viewer",
    description: "Browse EUP clothing components.",
    keywords: "eup ped clothing viewer",
    category: "tool",
    group: "creator",
  },
  {
    path: "/weapon-viewer",
    title: "Weapon Viewer",
    description: "Inspect FiveM weapon assets.",
    keywords: "weapon gun viewer",
    category: "tool",
    group: "creator",
  },
  {
    path: "/item-image",
    title: "Item Image",
    description: "Generate inventory item images with AI.",
    keywords: "item image inventory icon ai",
    category: "tool",
    group: "creator",
  },
  {
    path: "/badge-maker",
    title: "Badge Maker",
    description: "Generate badges, crests, and emblems for liveries and crews.",
    keywords: "badge crest emblem logo ai generate",
    category: "tool",
    group: "creator",
  },
  {
    path: "/embroidery-tool",
    title: "Embroidery Tool",
    description: "Upload artwork and convert it into an embroidered patch.",
    keywords: "embroidery embroidered patch stitch thread sew logo badge ai upload",
    category: "tool",
    group: "creator",
  },
  {
    path: "/blinklab-3d",
    title: "3D BlinkLab",
    description: "Design a siren pattern and watch it play on your own .yft in 3D.",
    keywords: "blinklab 3d siren lightbar preview yft carcols pattern vehicle",
    category: "tool",
    group: "creator",
  },
  {
    path: "/modkit-viewer",
    title: "Modkit Viewer",
    description: "Preview kit parts, toggle extras and open doors on your own vehicle in 3D.",
    keywords: "modkit mod kit viewer parts extras doors liveries yft vehicle carcols",
    category: "tool",
    group: "creator",
  },
  {
    path: "/ink-lab",
    title: "Ink Lab",
    description: "Make a custom freemode tattoo from a prompt, reference or your own PNG and export a drop-in addon.",
    keywords: "ink tattoo tattoos body art design ai generate transparent addon overlay ped freemode",
    category: "tool",
    group: "creator",
  },
  {
    path: "/handling-diff",
    title: "Handling Diff",
    description: "Compare two handling.meta files field by field.",
    keywords: "handling diff compare meta vehicle fmass ftraction fbrake carcols field comparison",
    category: "tool",
    group: "creator",
  },
  {
    path: "/resmon",
    title: "Resmon Report",
    description: "Turn a resmon or txAdmin dump into a ranked performance report.",
    keywords: "resmon report performance server cpu frame time txadmin dump profile resource cost",
    category: "tool",
    group: "creator",
  },
  // Free, public server-owner tools — no account, no featureKey
  {
    path: "/free/server-cfg",
    title: "server.cfg Builder",
    description: "Generate or repair a FiveM server.cfg in dependency order.",
    keywords:
      "server cfg config ensure load order onesync maxclients enforcegamebuild licence key fivem boot",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/spawn-index",
    title: "Spawn Code Finder",
    description: "Every spawn code a vehicle pack declares, and how each one is known.",
    keywords:
      "spawn code spawn name vehicles.meta modelName duplicate spawn code qbcore vehicles.lua vehicle pack",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/clash-finder",
    title: "Clash Finder",
    description:
      "Which files and spawn codes two resources are fighting over, and which copy wins today.",
    keywords:
      "clash conflict collision duplicate stream namespace overwrite shadowed data_file handling load order which resource wins",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/stream-budget",
    title: "Stream Budget",
    description: "What a joining player downloads, and how much of it is wasted.",
    keywords:
      "stream budget download size first join slow ytd size duplicate assets resource size reduce download",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/spawn-rename",
    title: "Spawn Code Renamer",
    description: "Rename a vehicle everywhere its pack actually references it.",
    keywords:
      "rename spawn code model name vehicles.meta modelName txdName handlingId rename vehicle stream files collision refuse dry run",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/id-bench",
    title: "ID Bench",
    description: "Mod kit, siren and light ids that collide — and a safe renumber.",
    keywords:
      "carcols id mod kit modkit siren light carvariations duplicate id stock id out of range sentinel renumber ssla siren limit",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/joaat",
    title: "JOAAT Hash Generator",
    description: "Convert any name to its GetHashKey value.",
    keywords: "joaat hash gethashkey hash key model hash signed unsigned hex jenkins one at a time",
    category: "tool",
    group: "support",
  },
  {
    path: "/free/vector3",
    title: "Coordinates Helper",
    description: "Reformat GTA5 coords as vector3, vec3 & more.",
    keywords: "coordinates coords vector3 vec3 vector4 heading xyz location position format convert",
    category: "tool",
    group: "support",
  },
  // Support / docs
  {
    path: "/guides",
    title: "Guides",
    description: "Tutorials, how-tos, and tool guides.",
    keywords: "guides docs help tutorial how-to",
    category: "page",
    group: "support",
  },
  {
    path: "/guides/gta5",
    title: "Guides — GTA 5",
    description: "GTA-specific tutorials.",
    keywords: "gta gta5 guides tutorial",
    category: "page",
    group: "support",
  },
  {
    path: "/guides/tools",
    title: "Guides — Tools",
    description: "Per-tool walkthroughs.",
    keywords: "tools guides walkthrough",
    category: "page",
    group: "support",
  },

  // Account
  {
    path: "/account",
    title: "Account",
    description: "Subscription, billing, profile.",
    keywords: "account profile billing subscription pricing",
    category: "page",
    group: "account",
  },

  // Info
  {
    path: "/privacy-policy",
    title: "Privacy Policy",
    description: "How we handle your data.",
    keywords: "privacy data policy gdpr",
    category: "page",
    group: "info",
  },
  {
    path: "/terms",
    title: "Terms of Service",
    description: "Terms of using The Workshop.",
    keywords: "terms tos service legal",
    category: "page",
    group: "info",
  },
  {
    path: "/refund-policy",
    title: "Refund Policy",
    description: "Our refund rules.",
    keywords: "refund money back policy",
    category: "page",
    group: "info",
  },
  {
    path: "/cancellation-policy",
    title: "Cancellation Policy",
    description: "How to cancel your subscription.",
    keywords: "cancel cancellation subscription",
    category: "page",
    group: "info",
  },
];

export type WrenchGoal = {
  id: string;
  title: string;
  icon: "props" | "livery" | "optimize" | "scripts";
  destination: string;
};

export const WRENCH_GOALS: WrenchGoal[] = [
  { id: "props", title: "Create Props", icon: "props", destination: "/model-forge" },
  { id: "livery", title: "Create Liveries / Textures", icon: "livery", destination: "/paintbooth-studio" },
  { id: "optimize", title: "Optimize Assets", icon: "optimize", destination: "/pack-bench" },
  { id: "scripts", title: "Create Scripts", icon: "scripts", destination: "/code-smith" },
];
